import {
  CreateEventRequest,
  DeleteEventsRequest,
  EventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events/EventRecord.ts";
import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
}

interface WorkspaceEventsRequestBody {
  dataset_ids: string[];
  file_ids: string[];
  page_token?: string;
}

// Converts a URL fragment like "foo/bar" to a string like "FooBar"
function toTitleCase(urlFragment: string): string {
  return urlFragment
    .split("/")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join("");
}

export class EventService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createEvent(
    orgId: string,
    event: CreateEventRequest,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/events/create");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      // bigintReplacer keeps the bigint ns-epoch bounds full-precision on the wire.
      body: JSON.stringify(event, bigintReplacer),
      headers: robotoHeaders({
        resourceOwnerId: options?.resourceOwnerId,
        orgId,
      }),
    });
    return response.json<EventRecord>(parseTimestampsAsBigInt);
  }

  public async getEventById(
    eventId: string,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/events/id/${eventId}`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<EventRecord>(parseTimestampsAsBigInt);
  }

  /**
   * Fetch every event relevant to a visualization workspace, described by its data
   * sources. Events anywhere within `datasetIds` and `fileIds` are returned
   * transitively (the entity itself and all of its files, topics, and message paths);
   * a loose file additionally surfaces its parent dataset's own events, which the
   * server resolves from the file id. This is the viz-specific `/v1/viz/events` query,
   * not a general-purpose event lookup. Follows `next_token` to accumulate every page,
   * and returns `[]` without a request when there are no data sources (the backend
   * rejects an empty request with a 400).
   */
  public async getWorkspaceEvents(
    sources: {
      datasetIds?: string[];
      fileIds?: string[];
    },
    options?: Partial<Options>,
  ): Promise<EventRecord[]> {
    const datasetIds = sources.datasetIds ?? [];
    const fileIds = sources.fileIds ?? [];
    if (datasetIds.length === 0 && fileIds.length === 0) {
      return [];
    }

    const requestUrl = this.#httpClient.constructUrl("v1/viz/events");
    const items: EventRecord[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const body: WorkspaceEventsRequestBody = {
        dataset_ids: datasetIds,
        file_ids: fileIds,
        page_token: pageToken,
      };
      const response = await this.#httpClient.post(requestUrl, {
        signal: options?.abortSignal,
        body: JSON.stringify(body),
        headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      });
      const page = await response.json<PaginatedResponse<EventRecord>>(
        parseTimestampsAsBigInt,
      );
      items.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return items;
  }

  public async deleteEvent(
    eventId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(`v1/events/id/${eventId}`);
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    await response.throwIfError();
  }

  public async deleteEvents(
    eventIds: string[],
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl("v1/events/delete");
    const body: DeleteEventsRequest = { event_ids: eventIds };
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(body),
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    await response.throwIfError();
  }

  public async updateEvent(
    eventId: string,
    updates: UpdateEventRequest,
    options?: Partial<Options>,
  ): Promise<EventRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/events/id/${eventId}`);
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(updates),
    });
    return response.json<EventRecord>(parseTimestampsAsBigInt);
  }

  public async getTagsForOrg(options?: Partial<Options>): Promise<string[]> {
    return this.getAllForOrg("v1/events/tags", options);
  }

  public async getMetadataKeysForOrg(
    options?: Partial<Options>,
  ): Promise<string[]> {
    return this.getAllForOrg("v1/events/metadata/keys", options);
  }

  public async getNamesForOrg(options?: Partial<Options>): Promise<string[]> {
    return this.getAllForOrg("v1/events/names", options);
  }

  private async getAllForOrg(
    relativeUrl: string,
    options?: Partial<Options>,
  ): Promise<string[]> {
    const resource = relativeUrl.substring("v1/events/".length);
    if (!options?.resourceOwnerId) {
      throw Error(
        `get${toTitleCase(resource)}ForOrg requires an Org ID, none was provided`,
      );
    }

    const requestUrl = this.#httpClient.constructUrl(relativeUrl);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });

    return await response.json<string[]>();
  }
}
